/**
 * Templates Panel - Save and load configuration templates
 */

import { useState } from 'react';
import {
  Save,
  FolderOpen,
  Trash2,
  Clock,
  ChevronDown,
  ChevronUp,
  X,
  FileText,
} from 'lucide-react';
import { Button, useToast } from '../ui';
import { useConfigurator } from '../../hooks/useConfigurator';
import { useConfigTemplates, type ConfigTemplate } from '../../hooks/useConfigTemplates';
import { clsx } from 'clsx';

interface TemplatesPanelProps {
  className?: string;
}

export function TemplatesPanel({ className }: TemplatesPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');

  const { config, loadConfig } = useConfigurator();
  const { templates, saveTemplate, deleteTemplate } = useConfigTemplates();
  const { success, error } = useToast();

  const handleSave = () => {
    if (!templateName.trim()) {
      error('Please enter a template name');
      return;
    }

    saveTemplate(templateName.trim(), templateDesc.trim(), config);
    success(`Template "${templateName}" saved`);
    setTemplateName('');
    setTemplateDesc('');
    setShowSaveForm(false);
  };

  const handleLoad = (template: ConfigTemplate) => {
    loadConfig(template.config);
    success(`Loaded template "${template.name}"`);
    setIsOpen(false);
  };

  const handleDelete = (template: ConfigTemplate, e: React.MouseEvent) => {
    e.stopPropagation();
    deleteTemplate(template.id);
    success(`Deleted template "${template.name}"`);
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className={clsx('relative', className)}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="gap-2"
      >
        <FolderOpen className="h-4 w-4" />
        Templates
        {templates.length > 0 && (
          <span className="rounded-full bg-hermit-100 px-1.5 py-0.5 text-xs text-hermit-700">
            {templates.length}
          </span>
        )}
        {isOpen ? (
          <ChevronUp className="h-3 w-3" />
        ) : (
          <ChevronDown className="h-3 w-3" />
        )}
      </Button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Panel */}
          <div className="absolute right-0 top-full z-50 mt-2 w-80 rounded-lg border border-gray-200 bg-white shadow-lg">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <h3 className="font-medium text-gray-900">Configuration Templates</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Save Form */}
            {showSaveForm ? (
              <div className="border-b border-gray-100 p-4">
                <input
                  type="text"
                  placeholder="Template name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="mb-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-hermit-500 focus:outline-none focus:ring-1 focus:ring-hermit-500"
                  autoFocus
                />
                <textarea
                  placeholder="Description (optional)"
                  value={templateDesc}
                  onChange={(e) => setTemplateDesc(e.target.value)}
                  rows={2}
                  className="mb-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-hermit-500 focus:outline-none focus:ring-1 focus:ring-hermit-500"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSave} className="flex-1">
                    <Save className="mr-1.5 h-3.5 w-3.5" />
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowSaveForm(false);
                      setTemplateName('');
                      setTemplateDesc('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="border-b border-gray-100 p-3">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowSaveForm(true)}
                  className="w-full gap-2"
                >
                  <Save className="h-4 w-4" />
                  Save Current Configuration
                </Button>
              </div>
            )}

            {/* Template List */}
            <div className="max-h-64 overflow-y-auto">
              {templates.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  <FileText className="mx-auto mb-2 h-8 w-8 text-gray-300" />
                  No saved templates yet
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleLoad(template)}
                      className="group flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-gray-50"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {template.name}
                        </div>
                        {template.description && (
                          <div className="text-xs text-gray-500 truncate">
                            {template.description}
                          </div>
                        )}
                        <div className="mt-1 flex items-center gap-1 text-xs text-gray-400">
                          <Clock className="h-3 w-3" />
                          {formatDate(template.updatedAt)}
                          <span className="mx-1">·</span>
                          {template.config.nodes.length} nodes
                        </div>
                      </div>
                      <button
                        onClick={(e) => handleDelete(template, e)}
                        className="rounded p-1.5 text-gray-400 opacity-0 hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"
                        title="Delete template"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {templates.length > 0 && (
              <div className="border-t border-gray-100 px-4 py-2 text-xs text-gray-400">
                {templates.length} of 10 templates used
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
